import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { AlertTriangle, Loader2 } from 'lucide-react'

import { dramaApi, dramaQueryKeys } from '@/lib/api/drama'
import { dramaT } from '@/components/drama/i18n'
import { useDramaLang } from '@/lib/useDramaLang'

import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog'
import { toast } from '@/components/ui/toaster'

interface Props {
  projectId: string
  projectTitle: string
  trigger: React.ReactNode
}

export function DeleteProjectDialog({ projectId, projectTitle, trigger }: Props) {
  const [lang] = useDramaLang()
  const t = dramaT(lang)
  const [open, setOpen] = useState(false)
  const [confirmText, setConfirmText] = useState('')
  const queryClient = useQueryClient()
  const navigate = useNavigate()

  const { data: impact, isLoading } = useQuery({
    queryKey: ['drama-deletion-impact', projectId],
    queryFn: () => dramaApi.getDeletionImpact(projectId),
    enabled: open,
  })

  const deleteMutation = useMutation({
    mutationFn: () => dramaApi.deleteProject(projectId),
    onSuccess: () => {
      toast({ title: t('del.delete') })
      queryClient.invalidateQueries({ queryKey: dramaQueryKeys.list() })
      setOpen(false)
      navigate('/drama')
    },
    onError: (e: any) =>
      toast({
        title: t('toast.deleteFailed'),
        description: e?.response?.data?.detail || e.message,
        variant: 'destructive',
      }),
  })

  const canDelete = confirmText.trim() === projectTitle.trim()

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>{trigger}</DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <AlertTriangle className="w-5 h-5 text-destructive" />
            {t('del.titleConfirm', projectTitle)}
          </DialogTitle>
          <DialogDescription>{t('del.cannotUndo')}</DialogDescription>
        </DialogHeader>

        {isLoading ? (
          <div className="py-4 text-center">
            <Loader2 className="w-5 h-5 animate-spin mx-auto" />
          </div>
        ) : impact ? (
          <div className="space-y-1 text-sm">
            <div className="font-medium mb-2">{t('del.willDelete')}</div>
            <ul className="list-disc list-inside space-y-1 text-muted-foreground">
              <li>{t('del.episodes', impact.episodes_count, impact.episodes_total_size_mb.toFixed(1))}</li>
              <li>{t('del.segments', impact.segments_count)}</li>
              <li>{t('del.characters', impact.characters_count)}</li>
              <li>{t('del.plans', impact.edit_plans_count)}</li>
              <li>{t('del.renders', impact.renders_count)}</li>
            </ul>
            {impact.assets_in_library_count > 0 && (
              <div className="mt-3 flex items-start gap-1.5 text-xs text-emerald-600 dark:text-emerald-400">
                <span aria-hidden>✓</span>
                <span>{t('del.keptAssets', impact.assets_in_library_count)}</span>
              </div>
            )}
          </div>
        ) : null}

        <div className="space-y-2 pt-3">
          <Label className="text-xs">
            {t('del.confirmPrefix')}<code className="font-mono">{projectTitle}</code>{t('del.confirmSuffix')}
          </Label>
          <Input
            value={confirmText}
            onChange={(e) => setConfirmText(e.target.value)}
            placeholder={projectTitle}
          />
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => setOpen(false)}>
            {t('del.cancel')}
          </Button>
          <Button
            variant="destructive"
            disabled={!canDelete || deleteMutation.isPending}
            onClick={() => deleteMutation.mutate()}
          >
            {deleteMutation.isPending ? (
              <>
                <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" />
                {t('del.deleting')}
              </>
            ) : (
              t('del.delete')
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
